import { useState } from 'react';
import { Alert, Checkbox, Form, Modal } from 'antd';
import { useMutation } from '@apollo/client/react';
import { ENROLL_SHORT_TERM_ARRANGEMENT_OFFER } from 'mutations/shortTermArrangement';
import { CREATE_ZENDESK_CASE } from 'mutations/zendeskCaseCreation';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { SIZE, STATUS } from 'utilities/constants';
import SectionMessage from 'components/SectionMessage/SectionMessage';
import {
  copyZendeskLinkButton,
  zendeskUrlButton,
} from 'components/NoteBox/zendeskUtils';
import HardshipEligibilityScript from './HardshipEligibilityScript';
import EligibilityTable from '../EligibilityTable';

const HardshipEligibilityModal = ({
  eligible,
  eligibilityChecks,
  hasAutopay,
  customerId,
  accountId,
  enableStarrEnrollment,
  nextStatementCloseDate,
  hide,
  ...modalProps
}) => {
  const [form] = Form.useForm();
  const { customerInfo } = useCustomerInfo();

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [zendeskUrl, setZendeskUrl] = useState('');
  const [zendeskLinkCopied, setZendeskLinkCopied] = useState(false);

  const [enrollStarr, enrollStarrMutation] = useMutation(
    ENROLL_SHORT_TERM_ARRANGEMENT_OFFER,
    {
      refetchQueries: ['StarrEligibility'],
      notifyOnNetworkStatusChange: true,
    },
  );

  const [createZendeskTicket, createZendeskTicketMutation] = useMutation(
    CREATE_ZENDESK_CASE,
    {
      refetchQueries: ['AccountHistory'],
      notifyOnNetworkStatusChange: true,
    },
  );

  const handleSubmit = async () => {
    try {
      await form.validateFields();

      if (enableStarrEnrollment) {
        await enrollStarr({
          variables: {
            customerId,
            accountId,
            numberOfCycles: 1,
            minimumPaymentDue: 1500,
          },
        });
      } else {
        const response = await createZendeskTicket({
          variables: {
            note: 'starr eligible',
            email: customerInfo.email,
            name: customerInfo.fullName,
            cycleDate: nextStatementCloseDate,
            caseType: 'SAH_HARDSHIP_STARR',
          },
        });
        setZendeskUrl(response?.data?.caseWorkCreateCase.url);
      }

      setShowSuccessMessage(true);
    } catch (error) {
      console.error({ error });
    }
  };

  return (
    <Modal
      {...modalProps}
      centered
      width={700}
      title={`Hardship Eligibility ${eligible ? '' : 'Criteria'}`}
      onOk={handleSubmit}
      okText="Submit"
      cancelButtonProps={{ hidden: !eligible }}
      okButtonProps={{
        loading: enableStarrEnrollment
          ? enrollStarrMutation.loading
          : createZendeskTicketMutation.loading,
        hidden: !eligible,
      }}
    >
      {eligible ? (
        <>
          <HardshipEligibilityScript hasAutopay={hasAutopay} />
          <Form form={form}>
            <Form.Item
              valuePropName="checked"
              name="acknowledgement"
              rules={[
                {
                  /* eslint-disable no-unused-vars */
                  validator: (_, value) => {
                    return new Promise((resolve, reject) => {
                      if (!value) {
                        reject();
                      }
                      resolve();
                    });
                  },
                  message: 'Please acknowledge your authorization',
                },
              ]}
            >
              <Checkbox data-testid="acknowledge-button">
                By clicking this button, I acknowledge the customer's hardship
                is less than 6 months.
              </Checkbox>
            </Form.Item>
            {showSuccessMessage && (
              <SectionMessage
                buttons={
                  enableStarrEnrollment
                    ? []
                    : [
                        zendeskUrlButton(zendeskUrl),
                        copyZendeskLinkButton(
                          zendeskUrl,
                          zendeskLinkCopied,
                          setZendeskLinkCopied,
                        ),
                      ]
                }
                verticalButtons={!enableStarrEnrollment}
                data-testid="success-message"
                status={STATUS.SUCCESS}
                size={SIZE.MD}
                text={
                  enableStarrEnrollment
                    ? 'Customer enrolled in STARR program'
                    : 'Zendesk ticket created!'
                }
                cover={showSuccessMessage}
                canClose={true}
                handleClose={() => {
                  setShowSuccessMessage(false);
                  form.resetFields();
                  hide();
                }}
              />
            )}
            {(enableStarrEnrollment
              ? enrollStarrMutation?.error
              : createZendeskTicketMutation?.error) && (
              <Alert
                data-testid="enroll-error-alert"
                banner
                type="error"
                message={
                  enableStarrEnrollment
                    ? 'Failed to enroll customer in STARR program. Please try again.'
                    : 'Failed to create Zendesk ticket. Please try again.'
                }
              />
            )}
          </Form>
        </>
      ) : (
        <EligibilityTable
          eligible={eligible}
          eligibilityChecks={eligibilityChecks}
        />
      )}
    </Modal>
  );
};

export default HardshipEligibilityModal;
